import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import ImageUploader from "@/components/ImageUploader";
import RichEditor from "@/components/RichEditor";
import {
  FileText, Plus, Edit2, Trash2, Loader2, X, Eye, EyeOff, Archive, RotateCcw,
  Check, Linkedin,
} from "lucide-react";

// ------ Fixed, controlled category list (no free text allowed) ------
const CATEGORIES = [
  { key: "ai_governance",   ar: "حوكمة الذكاء الاصطناعي", en: "AI Governance" },
  { key: "gdpr",            ar: "GDPR",                    en: "GDPR" },
  { key: "data_protection", ar: "حماية البيانات",          en: "Data Protection" },
  { key: "grc",             ar: "GRC",                     en: "GRC" },
  { key: "digital_health",  ar: "الصحة الرقمية",            en: "Digital Health" },
];
const CATEGORY_KEYS = CATEGORIES.map((c) => c.key);

/** Encode selected keys (any order, may be old free text) → canonical CSV. */
function encodeCats(list) {
  return (list || []).filter((k) => CATEGORY_KEYS.includes(k)).join(",");
}
/** Decode CSV → array; fuzzy-matches legacy free-text values. */
function decodeCats(raw) {
  if (!raw) return [];
  const parts = String(raw).split(",").map((s) => s.trim().toLowerCase().replace(/\s|-/g, "_"));
  const hits = [];
  for (const p of parts) {
    if (!p) continue;
    if (CATEGORY_KEYS.includes(p)) { hits.push(p); continue; }
    if (p.includes("ai")) hits.push("ai_governance");
    else if (p.includes("gdpr")) hits.push("gdpr");
    else if (p.includes("data") || p.includes("privacy")) hits.push("data_protection");
    else if (p.includes("grc")) hits.push("grc");
    else if (p.includes("digital") || p.includes("health")) hits.push("digital_health");
  }
  return [...new Set(hits)];
}
function catLabel(key, lang) {
  const c = CATEGORIES.find((x) => x.key === key);
  if (!c) return key;
  return lang === "ar" ? c.ar : c.en;
}

const EMPTY = {
  title_ar: "", title_en: "", excerpt_ar: "", excerpt_en: "",
  body_ar: "", body_en: "", cover_image: "", cover_image_path: null,
  tag_ar: "", tag_en: "", category_ar: "", category_en: "",
  linkedin_url: "",
  published: true,
};

export default function AdminArticles() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [catFilter, setCatFilter] = useState("");
  const [tagDraft, setTagDraft] = useState("");

  const load = () => api.get("/admin/articles").then((r) => setRows(r.data || []));
  useEffect(() => { load(); }, []);

  const selectedCats = editing ? decodeCats(editing.category_en) : [];
  const selectedTags = editing
    ? String(editing.tag_en || "").split(",").map((s) => s.trim()).filter(Boolean)
    : [];

  const toggleCat = (key) => {
    const next = selectedCats.includes(key)
      ? selectedCats.filter((k) => k !== key)
      : [...selectedCats, key];
    const csv = encodeCats(next);
    // Store the canonical CSV in BOTH language fields so old lookups still work.
    setEditing((e) => ({ ...e, category_en: csv, category_ar: csv }));
  };
  const addTag = () => {
    const t = tagDraft.trim();
    if (!t || selectedTags.includes(t)) { setTagDraft(""); return; }
    const next = [...selectedTags, t].join(",");
    setEditing((e) => ({ ...e, tag_en: next, tag_ar: next }));
    setTagDraft("");
  };
  const removeTag = (t) => {
    const next = selectedTags.filter((x) => x !== t).join(",");
    setEditing((e) => ({ ...e, tag_en: next, tag_ar: next }));
  };

  const filterCats = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => decodeCats(r.category_en).forEach((k) => s.add(k)));
    return [...s];
  }, [rows]);

  const visible = useMemo(
    () => rows.filter((r) => {
      const arch = showArchived ? true : !r.is_archived;
      if (!catFilter) return arch;
      return arch && decodeCats(r.category_en).includes(catFilter);
    }),
    [rows, showArchived, catFilter],
  );

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (a) => setEditing({ ...EMPTY, ...a });
  const close = () => { setEditing(null); setTagDraft(""); };

  const save = async () => {
    if (!editing.title_en || !editing.title_ar) {
      toast.error("Titles are required (AR + EN)"); return;
    }
    if (selectedCats.length === 0) {
      toast.error("Please select at least one category"); return;
    }
    setSaving(true);
    try {
      const payload = { ...editing };
      if (editing.id) await api.put(`/admin/articles/${editing.id}`, payload);
      else await api.post("/admin/articles", payload);
      toast.success("Saved"); close(); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const togglePublish = async (a) => {
    try {
      await api.put(`/admin/articles/${a.id}`, { ...a, published: !a.published });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const archive = async (a) => {
    if (!window.confirm(`Archive "${a.title_en}"?`)) return;
    try { await api.post(`/admin/articles/${a.id}/archive`); toast.success("Archived"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const restore = async (a) => {
    try { await api.post(`/admin/articles/${a.id}/restore`); toast.success("Restored"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (a) => {
    if (!window.confirm("Permanently delete? Prefer archive instead.")) return;
    try { await api.delete(`/admin/articles/${a.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-articles">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <span className="eyebrow">Content</span>
          <h1 className="mt-2 text-3xl text-navy flex items-center gap-3">
            <FileText size={26} className="text-gold" /> Knowledge Library
          </h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <select className="field-box !py-2" value={catFilter}
                  onChange={(e) => setCatFilter(e.target.value)}
                  data-testid="art-filter-cat">
            <option value="">All categories</option>
            {filterCats.map((k) => (
              <option key={k} value={k}>{catLabel(k, "en")}</option>
            ))}
          </select>
          <label className="text-xs text-navy/70 inline-flex items-center gap-2">
            <input type="checkbox" checked={showArchived}
                   onChange={(e) => setShowArchived(e.target.checked)}
                   data-testid="art-show-archived" />
            Show archived
          </label>
          <button onClick={openNew} className="btn-gold" data-testid="art-add">
            <Plus size={14} /> New article
          </button>
        </div>
      </div>
      <span className="divider-gold" />

      <div className="card-elev divide-y divide-navy/10">
        {visible.length === 0 ? (
          <div className="p-8 text-center text-navy/50">No articles yet.</div>
        ) : visible.map((a) => {
          const cats = decodeCats(a.category_en);
          return (
            <div key={a.id} className="p-5 flex items-center gap-4"
                 data-testid={`art-row-${a.id}`}>
              {a.cover_image ? (
                <img src={a.cover_image} alt=""
                     className="w-24 aspect-video object-cover rounded-sm border border-navy/10" />
              ) : (
                <div className="w-24 aspect-video bg-cream border border-navy/10 rounded-sm flex items-center justify-center text-navy/30 text-[10px]">
                  no image
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-navy font-medium truncate">{a.title_en}</div>
                <div className="text-xs text-navy/60 flex flex-wrap items-center gap-2 mt-1">
                  <span>{a.title_ar}</span>
                  {cats.map((k) => (
                    <span key={k}
                          className="chip !bg-cream !text-navy !border-navy/20 !py-0 !px-1.5 text-[10px]">
                      {catLabel(k, "en")}
                    </span>
                  ))}
                  <span className="text-navy/40">· {new Date(a.created_at).toLocaleDateString()}</span>
                  {a.linkedin_url && (
                    <span className="inline-flex items-center gap-1"
                          style={{ color: "#0A66C2" }}>
                      <Linkedin size={11} /> LinkedIn
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 whitespace-nowrap">
                {a.is_archived ? (
                  <Badge className="bg-navy/20 text-navy/70"><Archive size={11} className="inline me-1" />Archived</Badge>
                ) : a.published ? (
                  <Badge className="bg-emerald-100 text-emerald-800"><Eye size={11} className="inline me-1" />Published</Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-800"><EyeOff size={11} className="inline me-1" />Draft</Badge>
                )}
                {!a.is_archived && (
                  <button onClick={() => togglePublish(a)}
                          className="text-navy/70 hover:text-gold p-1.5"
                          title={a.published ? "Save as draft" : "Publish"}
                          data-testid={`art-toggle-${a.id}`}>
                    {a.published ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                )}
                <button onClick={() => openEdit(a)} className="text-navy/70 hover:text-gold p-1.5" data-testid={`art-edit-${a.id}`}><Edit2 size={14} /></button>
                {a.is_archived ? (
                  <button onClick={() => restore(a)} className="text-emerald-700 p-1.5" title="Restore" data-testid={`art-restore-${a.id}`}><RotateCcw size={14} /></button>
                ) : (
                  <button onClick={() => archive(a)} className="text-amber-700 p-1.5" title="Archive" data-testid={`art-archive-${a.id}`}><Archive size={14} /></button>
                )}
                <button onClick={() => remove(a)} className="text-red-600/80 p-1.5" data-testid={`art-del-${a.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-navy/70 backdrop-blur-sm z-50 p-4 overflow-y-auto" onClick={close}>
          <div className="bg-white max-w-4xl mx-auto rounded-md border border-gold/30 p-8 my-6"
               onClick={(e) => e.stopPropagation()} data-testid="art-editor">
            <div className="flex justify-between">
              <h2 className="text-2xl text-navy">{editing.id ? "Edit article" : "New article"}</h2>
              <button onClick={close} className="text-navy/50"><X size={18} /></button>
            </div>
            <span className="divider-gold" />

            {/* Titles */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-navy/70 mb-1 block">Title (AR)</label>
                <input className="field-box w-full" placeholder="عنوان المقال"
                       value={editing.title_ar}
                       onChange={(e) => setEditing({ ...editing, title_ar: e.target.value })}
                       data-testid="art-title-ar" />
              </div>
              <div>
                <label className="text-xs font-semibold text-navy/70 mb-1 block">Title (EN)</label>
                <input className="field-box w-full" placeholder="Article title"
                       value={editing.title_en}
                       onChange={(e) => setEditing({ ...editing, title_en: e.target.value })}
                       data-testid="art-title-en" />
              </div>
            </div>

            {/* Categories — controlled multi-select */}
            <div className="mt-5">
              <label className="text-xs font-semibold text-navy/70 mb-2 block">
                Categories <span className="text-red-600">*</span>
                <span className="ms-2 text-[11px] font-normal text-navy/50">
                  (required — select one or more)
                </span>
              </label>
              <div className="flex flex-wrap gap-2" data-testid="art-cats">
                {CATEGORIES.map((c) => {
                  const on = selectedCats.includes(c.key);
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => toggleCat(c.key)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-xs font-medium transition-colors ${
                        on
                          ? "bg-navy text-gold border-navy"
                          : "bg-white text-navy border-navy/25 hover:border-gold"
                      }`}
                      data-testid={`art-cat-${c.key}`}
                    >
                      {on && <Check size={12} />}
                      {c.en}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tags — separate optional multi-tag input */}
            <div className="mt-5">
              <label className="text-xs font-semibold text-navy/70 mb-2 block">
                Tags
                <span className="ms-2 text-[11px] font-normal text-navy/50">
                  (optional — press Enter to add)
                </span>
              </label>
              <div className="flex flex-wrap items-center gap-2 min-h-[42px] border border-navy/20 rounded-sm px-2 py-1.5 bg-white"
                   data-testid="art-tags">
                {selectedTags.map((t) => (
                  <span key={t}
                        className="inline-flex items-center gap-1 chip !bg-cream !text-navy !border-navy/20 !py-0.5 !px-2">
                    {t}
                    <button type="button" onClick={() => removeTag(t)}
                            className="text-navy/50 hover:text-red-600 ms-1"
                            data-testid={`art-tag-rm-${t}`}>
                      <X size={11} />
                    </button>
                  </span>
                ))}
                <input
                  className="flex-1 min-w-[120px] border-0 outline-none text-sm bg-transparent"
                  placeholder="Add a tag…"
                  value={tagDraft}
                  onChange={(e) => setTagDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); } }}
                  onBlur={addTag}
                  data-testid="art-tag-input"
                />
              </div>
            </div>

            {/* LinkedIn URL */}
            <div className="mt-5">
              <label className="text-xs font-semibold text-navy/70 mb-1 flex items-center gap-1">
                <Linkedin size={12} style={{ color: "#0A66C2" }} />
                LinkedIn Article URL
                <span className="ms-1 text-[11px] font-normal text-navy/50">
                  (paste the original LinkedIn post URL, if any)
                </span>
              </label>
              <input className="field-box w-full"
                     placeholder="https://www.linkedin.com/pulse/…"
                     value={editing.linkedin_url || ""}
                     onChange={(e) => setEditing({ ...editing, linkedin_url: e.target.value })}
                     data-testid="art-linkedin" />
            </div>

            {/* Cover image */}
            <div className="mt-5">
              <label className="text-xs font-semibold text-navy/70 mb-1 block">
                Cover image
                <span className="ms-1 text-[11px] font-normal text-navy/50">
                  (optional — recommended 1600 × 900 px, 16:9)
                </span>
              </label>
              <ImageUploader
                value={editing.cover_image || ""}
                folder="articles"
                testId="art-cover"
                onChange={(url, path) => setEditing({ ...editing, cover_image: url || "", cover_image_path: path })}
              />
            </div>

            {/* Excerpt */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
              <div>
                <label className="text-xs font-semibold text-navy/70 mb-1 block">
                  Excerpt (AR)
                  <span className="ms-1 text-[11px] font-normal text-navy/50">
                    — Short summary displayed in the Knowledge Library card
                  </span>
                </label>
                <textarea className="field-box min-h-[80px] w-full"
                          value={editing.excerpt_ar || ""}
                          onChange={(e) => setEditing({ ...editing, excerpt_ar: e.target.value })}
                          data-testid="art-excerpt-ar" />
              </div>
              <div>
                <label className="text-xs font-semibold text-navy/70 mb-1 block">
                  Excerpt (EN)
                  <span className="ms-1 text-[11px] font-normal text-navy/50">
                    — Short summary displayed in the Knowledge Library card
                  </span>
                </label>
                <textarea className="field-box min-h-[80px] w-full"
                          value={editing.excerpt_en || ""}
                          onChange={(e) => setEditing({ ...editing, excerpt_en: e.target.value })}
                          data-testid="art-excerpt-en" />
              </div>
            </div>

            {/* Full content */}
            <div className="mt-5">
              <label className="text-xs font-semibold text-navy/70 mb-2 block">
                Full article content
                <span className="ms-2 text-[11px] font-normal text-navy/50">
                  (optional — leave empty when the article lives on LinkedIn only)
                </span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <RichEditor testId="art-body-ar" dir="rtl" value={editing.body_ar}
                            onChange={(html) => setEditing({ ...editing, body_ar: html })} />
                <RichEditor testId="art-body-en" dir="ltr" value={editing.body_en}
                            onChange={(html) => setEditing({ ...editing, body_en: html })} />
              </div>
            </div>

            <label className="mt-5 flex items-center gap-2 text-navy">
              <input type="checkbox" className="w-5 h-5 accent-gold"
                     checked={!!editing.published}
                     onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                     data-testid="art-publish" />
              Publish immediately
            </label>

            <div className="mt-6 flex gap-3">
              <button onClick={save} disabled={saving} className="btn-gold" data-testid="art-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save
              </button>
              <button onClick={close} className="btn-outline-navy">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
